/**
 * System Stats Hook - Vertical Feature Slice Example
 *
 * Demonstrates the TanStack Start migration pattern:
 * 1. Server function (stats.server.ts) handles server-side logic
 * 2. TanStack Query hook provides client-side state management
 * 3. UI component consumes the hook for reactive data
 *
 * This pattern replaces the legacy class-based service pattern.
 */

import { useQuery } from "@tanstack/react-query";
import { getSystemStats, getUserActivitySummary } from "../server/stats";

/**
 * Query keys for system stats (following centralized pattern)
 */
export const systemStatsKeys = {
  all: ["systemStats"] as const,
  stats: () => [...systemStatsKeys.all, "stats"] as const,
  activity: (userId: string) =>
    [...systemStatsKeys.all, "activity", userId] as const,
};

/**
 * Hook to fetch system statistics
 *
 * @example
 * ```tsx
 * function StatsPanel() {
 *   const { data, isLoading } = useSystemStats();
 *   return <div>{data?.totalUsers} users</div>
 * }
 * ```
 */
export function useSystemStats() {
  return useQuery({
    queryKey: systemStatsKeys.stats(),
    queryFn: async () => {
      const result = await getSystemStats({ data: {} });
      if (result.error) {
        throw new Error(result.error);
      }
      return result.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60, // Refetch every minute
  });
}

/**
 * Hook to fetch user activity summary
 *
 * @param userId - The user ID to fetch activity for
 * @param accessToken - The user's access token for auth
 */
export function useUserActivity(userId: string, accessToken: string) {
  return useQuery({
    queryKey: systemStatsKeys.activity(userId),
    queryFn: async () => {
      const result = await getUserActivitySummary({
        data: { userId, accessToken },
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result.data;
    },
    enabled: !!userId && !!accessToken,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}
